"use client";

import { useCallback, useEffect, useRef, useState } from "react";
import { Inter } from "next/font/google";
import styles from "./HoverWord.module.scss";
import { DesignOverlay } from "./DesignOverlay";
import { CodeOverlay } from "./CodeOverlay";
import { FlowOverlay } from "./FlowOverlay";
import { FigTag } from "./FigTag";
import { EXIT_MS } from "./shared";
import {
  claimStage,
  overlayEnded,
  overlayStarted,
  registerHintHost,
  releaseStage,
} from "./hintScheduler";

/* Figma's UI font — the design overlay quotes Figma chrome (frame label,
   dims badge, cursor name tag) and those must not render in the site's
   own faces. Loaded lazily on first use, never preloaded. */
const inter = Inter({
  subsets: ["latin"],
  weight: ["400", "500"],
  variable: "--font-figma",
  display: "swap",
  preload: false,
});

export type WordKind = "design" | "code" | "flow";

const OVERLAYS = {
  design: DesignOverlay,
  code: CodeOverlay,
  flow: FlowOverlay,
} as const;

/* Touch has no leave event, so a tap plays one performance that bows out
   on its own: each hold = when the performance lands + a dwell to read it.
   design lands ~0.5s (the arriving cursor's glide) + 1.5s; code finishes
   typing ~1.8s (IntelliSense included) + 1.6s of blinking caret; flow's
   run lands ~1.9s + 1.9s to read the finished canvas. */
const HOLD_MS: Record<WordKind, number> = {
  design: 2000,
  code: 3400,
  flow: 3800,
};

/* How long each hint stays up when the scheduler wakes it: the cursor
   finishes its stroll, the caret blinks a few times, Clawd gets an
   emergence plus a few idle beats (his retreat is timed from this). */
const HINT_MS: Record<WordKind, number> = {
  design: 4000,
  code: 3200,
  flow: 4200,
};

/* How long an interrupted hint takes to bow out (Clawd's scurry home is
   the longest piece). */
const HINT_OUT_MS = 280;

/* The design hint's stroll, as (time, x%, y%) knots over the word box:
   in from the lower left, a slow read across the middle (the knots bunch
   up around 62-72%), then away past the top-right corner. */
const STROLL: Array<[number, number, number]> = [
  [0, -4, 88],
  [0.14, 14, 56],
  [0.3, 32, 72],
  [0.48, 54, 38],
  [0.62, 61, 60],
  [0.72, 63, 56],
  [0.88, 84, 32],
  [1, 101, 12],
];

/* Time-parameterised Catmull-Rom (Barry-Goldman, knot times as the
   parameter): position AND velocity are continuous through every knot,
   so the cursor eases through waypoints instead of stopping at each one
   the way per-keyframe CSS easing does. Sampled into linear keyframes. */
type Pt = [number, number];

function strollFrames(w: number, h: number, samples = 60): Keyframe[] {
  const T = STROLL.map(([t]) => t);
  const P: Pt[] = STROLL.map(([, x, y]) => [(x / 100) * w, (y / 100) * h]);
  /* mirrored phantom knots give the end segments their tangents */
  const n = P.length;
  const mirror = (a: Pt, b: Pt): Pt => [2 * a[0] - b[0], 2 * a[1] - b[1]];
  const KT = [2 * T[0] - T[1], ...T, 2 * T[n - 1] - T[n - 2]];
  const KP = [mirror(P[0], P[1]), ...P, mirror(P[n - 1], P[n - 2])];
  const mix = (a: Pt, b: Pt, ta: number, tb: number, t: number): Pt => {
    const u = (t - ta) / (tb - ta);
    return [a[0] + (b[0] - a[0]) * u, a[1] + (b[1] - a[1]) * u];
  };
  const at = (t: number): Pt => {
    let i = 1;
    while (i < n - 1 && t > KT[i + 1]) i++;
    const [t0, t1, t2, t3] = [KT[i - 1], KT[i], KT[i + 1], KT[i + 2]];
    const [p0, p1, p2, p3] = [KP[i - 1], KP[i], KP[i + 1], KP[i + 2]];
    const a1 = mix(p0, p1, t0, t1, t);
    const a2 = mix(p1, p2, t1, t2, t);
    const a3 = mix(p2, p3, t2, t3, t);
    const b1 = mix(a1, a2, t0, t2, t);
    const b2 = mix(a2, a3, t1, t3, t);
    return mix(b1, b2, t1, t2, t);
  };
  return Array.from({ length: samples + 1 }, (_, s) => {
    const t = s / samples;
    const [x, y] = at(t);
    return { offset: t, transform: `translate(${x}px, ${y}px)` };
  });
}

/* Each keyword impersonates its tool. "design" becomes a selected Figma
   frame under the visitor's multiplayer cursor; "code" is selected,
   deleted and retyped in VS Code, IntelliSense popup included; "AI
   workflows" gives way to an n8n automation that builds and runs.
   All crisp DOM/SVG — the overlay remounts per play so the sequence
   replays, every play has a mirrored exit (EXIT_MS), and only one word
   may perform at a time (claiming the stage stops the other cleanly).
   Fine pointers play on hover; coarse pointers play on tap and
   auto-dismiss. On every screen each word occasionally hints (the
   strolling Figma cursor, a parked caret, Clawd emerging) via the shared
   scheduler. Plain text under reduced motion. */
export function HoverWord({
  kind,
  children,
}: {
  kind: WordKind;
  children: string;
}) {
  const hostRef = useRef<HTMLSpanElement>(null);
  const posRef = useRef({ x: 0, y: 0, seeded: false });
  const [mode, setMode] = useState<"off" | "hover" | "tap">("off");
  const [hot, setHot] = useState(false);
  const [shown, setShown] = useState(false);
  const [hinting, setHinting] = useState(false);
  const [hintOut, setHintOut] = useState(false);
  const [run, setRun] = useState(0);
  const hideTimer = useRef(0);
  const holdTimer = useRef(0);
  const hintTimer = useRef(0);
  const hintRef = useRef<HTMLSpanElement>(null);
  const shownRef = useRef(false);
  const visibleRef = useRef(false);

  /* The clean dismissal every path shares: hover-leave, tap timeout, and
     being bumped off the stage by another word starting its performance. */
  const stopCleanly = useCallback(() => {
    window.clearTimeout(holdTimer.current);
    setHot(false);
    hideTimer.current = window.setTimeout(() => setShown(false), EXIT_MS);
  }, []);

  /* A hint cut short by a play bows out instead of blinking away: the
     wrapper fades, and Clawd scurries home from wherever he stands (his
     live position is read back, so a half-emerged Clawd turns around). */
  const endHint = useCallback(() => {
    window.clearTimeout(hintTimer.current);
    const pet = hintRef.current?.querySelector<HTMLElement>(
      `.${styles.petSprite}`,
    );
    if (pet) {
      const from = getComputedStyle(pet).left;
      for (const a of pet.getAnimations()) a.cancel();
      pet.animate([{ left: from }, { left: "-0.9em" }], {
        duration: HINT_OUT_MS,
        easing: "steps(4)",
        fill: "forwards",
      });
    }
    setHintOut(true);
    hintTimer.current = window.setTimeout(() => {
      setHinting(false);
      setHintOut(false);
    }, HINT_OUT_MS);
  }, []);

  useEffect(() => {
    shownRef.current = shown;
    if (!shown) return;
    overlayStarted();
    return () => {
      overlayEnded();
      releaseStage(stopCleanly);
    };
  }, [shown, stopCleanly]);

  const track = (e: React.PointerEvent) => {
    const host = hostRef.current;
    if (!host) return;
    const r = host.getBoundingClientRect();
    posRef.current.x = e.clientX - r.left;
    posRef.current.y = e.clientY - r.top;
    posRef.current.seeded = true;
  };

  useEffect(() => {
    const motion = window.matchMedia("(prefers-reduced-motion: no-preference)");
    const fine = window.matchMedia("(pointer: fine)");
    const coarse = window.matchMedia("(pointer: coarse)");
    const queries = [motion, fine, coarse];
    const update = () =>
      setMode(
        !motion.matches
          ? "off"
          : fine.matches
            ? "hover"
            : coarse.matches
              ? "tap"
              : "off",
      );
    update();
    queries.forEach((mq) => mq.addEventListener("change", update));
    return () => {
      queries.forEach((mq) => mq.removeEventListener("change", update));
      window.clearTimeout(hideTimer.current);
      window.clearTimeout(holdTimer.current);
    };
  }, []);

  /* Hints are rationed by the shared scheduler — this word only offers
     itself while it's actually on screen and not mid-performance. They
     run on every pointer kind; only reduced motion stays hint-free. */
  useEffect(() => {
    if (mode === "off") return;
    const el = hostRef.current;
    if (!el) return;
    const io = new IntersectionObserver(
      ([entry]) => {
        visibleRef.current = entry.isIntersecting;
      },
      { threshold: 0.6 },
    );
    io.observe(el);
    const unregister = registerHintHost({
      visible: () => visibleRef.current,
      busy: () => shownRef.current,
      wake: () => {
        setHintOut(false);
        setHinting(true);
        window.clearTimeout(hintTimer.current);
        hintTimer.current = window.setTimeout(
          () => setHinting(false),
          HINT_MS[kind],
        );
        return HINT_MS[kind];
      },
      hide: endHint,
    });
    return () => {
      io.disconnect();
      unregister();
      window.clearTimeout(hintTimer.current);
      setHinting(false);
    };
  }, [mode, kind, endHint]);

  /* The stroll is measured against this word's own box, so it plays
     through WAAPI rather than as a percentage keyframe in the scss. */
  useEffect(() => {
    if (!hinting || kind !== "design") return;
    const host = hostRef.current;
    const cursor = hintRef.current?.firstElementChild as HTMLElement | null;
    if (!host || !cursor) return;
    const { width, height } = host.getBoundingClientRect();
    const anim = cursor.animate(strollFrames(width, height), {
      duration: HINT_MS.design,
      easing: "cubic-bezier(0.45, 0, 0.55, 1)",
      fill: "both",
    });
    return () => anim.cancel();
  }, [hinting, kind]);

  const onEnter = (e: React.PointerEvent) => {
    claimStage(stopCleanly);
    track(e);
    window.clearTimeout(hideTimer.current);
    setRun((r) => r + 1);
    setShown(true);
    setHot(true);
  };
  const onLeave = () => {
    setHot(false);
    hideTimer.current = window.setTimeout(() => setShown(false), EXIT_MS);
  };

  /* click, not pointerdown — a touch that turns into a scroll never
     lands here. Re-tapping mid-play restarts the sequence. */
  const onTap = (e: React.MouseEvent) => {
    claimStage(stopCleanly);
    const host = hostRef.current;
    if (host) {
      const r = host.getBoundingClientRect();
      posRef.current.x = e.clientX - r.left;
      posRef.current.y = e.clientY - r.top;
      posRef.current.seeded = true;
    }
    window.clearTimeout(hideTimer.current);
    window.clearTimeout(holdTimer.current);
    setRun((r) => r + 1);
    setShown(true);
    setHot(true);
    holdTimer.current = window.setTimeout(() => {
      setHot(false);
      hideTimer.current = window.setTimeout(() => setShown(false), EXIT_MS);
    }, HOLD_MS[kind]);
  };

  const Overlay = OVERLAYS[kind];

  return (
    <span
      ref={hostRef}
      className={`${styles.word} ${inter.variable}`}
      data-kind={kind}
      data-hot={hot ? "1" : "0"}
      style={{ "--hw-exit": `${EXIT_MS}ms` } as React.CSSProperties}
      onPointerEnter={mode === "hover" ? onEnter : undefined}
      onPointerMove={mode === "hover" ? track : undefined}
      onPointerLeave={mode === "hover" ? onLeave : undefined}
      onClick={mode === "tap" ? onTap : undefined}
    >
      <span className={styles.text}>{children}</span>
      {mode !== "off" && hinting && (
        <span
          ref={hintRef}
          className={styles.hint}
          data-out={hintOut ? "1" : "0"}
          style={{ "--hint-ms": `${HINT_MS[kind]}ms` } as React.CSSProperties}
          aria-hidden
        >
          {kind === "code" ? (
            <span className={styles.hintCaret} />
          ) : kind === "design" ? (
            <span className={styles.hintCursor}>
              <svg width="19" height="28" viewBox="0 0 24 36" fill="none">
                <path
                  d="M5.65376 12.3673H5.46026L5.31717 12.4976L0.500002 16.8829L0.500002 1.19841L11.7841 12.3673H5.65376Z"
                  fill="var(--fig)"
                  stroke="#fff"
                  strokeWidth="1"
                />
              </svg>
              <FigTag className={styles.hintTag} size="sm" />
            </span>
          ) : kind === "flow" ? (
            /* Clawd, Claude Code's pixel mascot, transcribed 1:1 from the
               official art on a unit grid (1u = 2): body 8x6u, symmetric
               2x2u arms at mid-height, square 1u eyes inset 1u from top
               and sides, four 1x2u legs at the edges and at 2u/5u.
               Anthropic terracotta (--hw-clawd) as a quotation, like
               the Figma blue. Walks out from behind the word (the stage's
               left edge is the mask), stands beside the punctuation and
               idles. */
            <span className={styles.hintPet}>
              <span className={styles.petSprite}>
                <svg viewBox="0 0 24 16" fill="none">
                  <rect x="0" y="4" width="4" height="4" fill="var(--hw-clawd)" />
                  <rect x="4" y="0" width="16" height="12" fill="var(--hw-clawd)" />
                  <rect x="20" y="4" width="4" height="4" fill="var(--hw-clawd)" />
                  <g className={styles.petEyes}>
                    <rect x="6" y="2" width="2" height="2" fill="#191919" />
                    <rect x="16" y="2" width="2" height="2" fill="#191919" />
                  </g>
                  <g className={styles.petStepA}>
                    <rect x="4" y="12" width="2" height="4" fill="var(--hw-clawd)" />
                    <rect x="8" y="12" width="2" height="4" fill="var(--hw-clawd)" />
                    <rect x="14" y="12" width="2" height="4" fill="var(--hw-clawd)" />
                    <rect x="18" y="12" width="2" height="4" fill="var(--hw-clawd)" />
                  </g>
                  <g className={styles.petStepB}>
                    <rect x="6" y="12" width="2" height="4" fill="var(--hw-clawd)" />
                    <rect x="9" y="12" width="2" height="4" fill="var(--hw-clawd)" />
                    <rect x="13" y="12" width="2" height="4" fill="var(--hw-clawd)" />
                    <rect x="16" y="12" width="2" height="4" fill="var(--hw-clawd)" />
                  </g>
                </svg>
              </span>
            </span>
          ) : null}
        </span>
      )}
      {mode !== "off" && shown && (
        <Overlay
          key={run}
          hostRef={hostRef}
          posRef={posRef}
          hot={hot}
          text={children}
          tap={mode === "tap"}
        />
      )}
    </span>
  );
}
